/**
 * 请求/响应拦截器
 * 统一处理请求认证、响应错误、网络异常
 */

import type { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { showToast } from '../../helpers/toast';
import { getAccessToken, isTokenExpire, logout } from '../../helpers/oktaUtils';

// ============ 错误消息映射 ============

const HTTP_ERROR_MESSAGES: Record<number, string> = {
  400: '请求参数错误',
  401: '登录已过期，请重新登录',
  403: '没有操作权限',
  404: '请求的资源不存在',
  408: '请求超时',
  500: '服务器内部错误',
  502: '网关错误',
  503: '服务暂不可用',
  504: '网关超时',
};

const NETWORK_ERROR_MESSAGE = '网络连接失败，请检查后端服务是否启动';
const TIMEOUT_ERROR_MESSAGE = '请求超时，请稍后重试';
const UNKNOWN_ERROR_MESSAGE = '请求失败，请稍后重试';

// ============ Toast 配置 ============

// 防抖：避免短时间内重复显示相同错误
let lastErrorTime = 0;
let lastErrorMessage = '';
const ERROR_DEBOUNCE_MS = 2000;

/**
 * 显示错误 Toast（带防抖）
 */
const showErrorToast = (message: string): void => {
  const now = Date.now();
  // 相同错误消息在 2 秒内不重复显示
  if (message === lastErrorMessage && now - lastErrorTime < ERROR_DEBOUNCE_MS) {
    return;
  }
  lastErrorTime = now;
  lastErrorMessage = message;

  showToast.error(message);
};

// ============ Token 管理 ============

/**
 * 获取认证 Token
 * 使用 Okta access_token 或 localStorage 中的 token
 */
const getAuthToken = (): string | null => {
  // 优先使用 Okta access_token
  const oktaToken = getAccessToken();
  if (oktaToken) {
    return oktaToken;
  }

  // 从 localStorage 获取 token
  return localStorage.getItem('token') || null;
};

// ============ 请求拦截器 ============

/**
 * 请求拦截器 - 添加认证头
 */
const requestInterceptor = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  // 检查 token 是否过期
  const oktaToken = getAccessToken();
  if (oktaToken && isTokenExpire()) {
    // Token 过期，执行登出
    logout();
    return Promise.reject(new Error('Token expired')) as never;
  }

  // 添加认证头
  const token = getAuthToken();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

/**
 * 请求错误拦截器
 */
const requestErrorInterceptor = (error: AxiosError): Promise<never> => {
  console.error('[Request Error]', error);
  showErrorToast(UNKNOWN_ERROR_MESSAGE);
  return Promise.reject(error);
};

// ============ 响应拦截器 ============

/**
 * 响应拦截器 - 正常响应直接返回
 */
const responseInterceptor = (response: AxiosResponse): AxiosResponse => {
  return response;
};

/**
 * 响应错误拦截器 - 统一处理所有错误类型
 */
const responseErrorInterceptor = (error: AxiosError): Promise<never> => {
  let errorMessage = UNKNOWN_ERROR_MESSAGE;

  if (error.response) {
    // 服务器返回了错误响应
    const { status, data } = error.response;
    errorMessage = HTTP_ERROR_MESSAGES[status] || `请求失败 (${status})`;

    // 尝试从响应体获取更详细的错误信息
    if (data && typeof data === 'object' && 'message' in data) {
      const serverMessage = (data as { message?: string }).message;
      if (serverMessage) {
        errorMessage = serverMessage;
      }
    }

    // 401 特殊处理：可跳转登录页
    if (status === 401) {
      console.error('Unauthorized: Please login');
      logout();
    }

    console.error(`[HTTP Error ${status}]`, errorMessage);
  } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    // 请求超时
    errorMessage = TIMEOUT_ERROR_MESSAGE;
    console.error('[Timeout Error]', error.message);
  } else if (error.request) {
    // 网络错误：请求已发出但没有收到响应
    errorMessage = NETWORK_ERROR_MESSAGE;
    console.error('[Network Error]', '后端服务无响应，请检查服务是否启动');
  } else {
    // 请求配置错误
    console.error('[Request Config Error]', error.message);
  }

  // 显示错误提示
  showErrorToast(errorMessage);

  return Promise.reject(error);
};

// ============ 导出 ============

/**
 * 设置拦截器
 */
export const setupInterceptors = (instance: AxiosInstance): void => {
  // 请求拦截器
  instance.interceptors.request.use(requestInterceptor, requestErrorInterceptor);

  // 响应拦截器
  instance.interceptors.response.use(responseInterceptor, responseErrorInterceptor);
};
